import { logAuthFlowInfo, logAuthFlowWarning } from "./authFlowLogger";
import { getResponseRequestId } from "./authDiagnostics";
import { getIntentDetails } from "./intent";
import { getApiBaseUrl } from "../runtime/apiBaseUrl";

export const PORTAL_IDS = Object.freeze({
    application: "application",
    broker: "broker",
    lender: "lender",
    staff: "staff",
    investor: "investor",
    client: "client",
    referrer: "referrer",
});

export const RESOLUTION_STATUSES = Object.freeze({
    confirmed: "confirmed",
    denied: "denied",
    unresolved: "unresolved",
});

export const RESOLUTION_REASON_CODES = Object.freeze({
    intentNotAvailable: "intent_not_available",
    accountNotFound: "account_not_found",
    accessDenied: "access_denied",
    noAuthorizedPortals: "no_authorized_portals",
    noConfirmedDestination: "no_confirmed_destination",
});

export const RESOLUTION_NEXT_ACTIONS = Object.freeze({
    launchPortal: "launch_portal",
    choosePortal: "choose_portal",
    contactSupport: "contact_support",
    retry: "retry",
});

const VALID_PORTAL_IDS = new Set(Object.values(PORTAL_IDS));
const PORTAL_PRIORITY = Object.freeze(["broker", "lender", "investor", "staff", "client", "referrer", "application"]);
const DENIED_ACCESS_STATUSES = new Set([
    "inactive",
    "disabled",
    "suspended",
    "revoked",
    "archived",
    "future_not_enabled",
    "invalid_lender",
    "lender_inactive",
    "lender_disabled",
    "lender_suspended",
    "lender_revoked",
    "lender_archived",
    "invalid_investor",
    "investor_inactive",
    "investor_disabled",
    "investor_suspended",
    "investor_revoked",
    "investor_archived",
    "invalid_referrer",
    "referrer_inactive",
    "referrer_disabled",
    "referrer_suspended",
    "referrer_revoked",
    "referrer_archived",
    "referrer_agreement_not_accepted",
]);
const PORTAL_ACCESS_MODELS = Object.freeze({
    application: "users/applications",
    broker: "brokerageUsers",
    lender: "lenderUsers",
    staff: "staffUsers",
    investor: "investorUsers",
    client: "clients",
    referrer: "referrerUsers",
});
const HOSTINGER_DEV_APPLICATION_FORM_URL = "http://147.93.108.31";
const LIVE_BROKER_PORTAL_URL = "https://financialedge-broker-portal.vercel.app/broker";
const LIVE_XPORTAL_URL = "https://financialedge-xportal.vercel.app";
const LENDER_PORTAL_DEFAULT_PATH = "/dashboard";
const LOCAL_APPLICATION_FORM_URL = "http://localhost:3000";
const LOCAL_BROKER_PORTAL_URL = "http://localhost:3014/broker";
const LOCAL_XPORTAL_URL = "http://localhost:3002";
const LOCAL_STAFF_PORTAL_URL = "http://localhost:3010";
const LOCAL_CLIENT_PORTAL_URL = "http://localhost:3011";
const LOCAL_REFERRER_PORTAL_URL = "http://localhost:3012";
const LOCAL_IPORTAL_URL = "http://localhost:3013";
const DEV_APPLICATION_FORM_SOURCES = new Set([
    "frontend-form-dev",
    "hostinger-frontend-form",
]);
const XPORTAL_SOURCES = new Set([
    "xportal",
    "lender-portal",
]);

function normalisePortalId(value) {
    const portalId = String(value || "").trim().toLowerCase();
    return VALID_PORTAL_IDS.has(portalId) ? portalId : null;
}

function resolveConfiguredUrl(value, fallback = "/") {
    const trimmed = String(value || "").trim();
    return trimmed || fallback;
}

function normaliseSource(value) {
    return String(value || "").trim().toLowerCase();
}

function isXPortalSource(source) {
    return XPORTAL_SOURCES.has(normaliseSource(source));
}

function resolveDevApplicationFormUrl(source) {
    if (!DEV_APPLICATION_FORM_SOURCES.has(normaliseSource(source))) {
        return null;
    }

    return resolveConfiguredUrl(
        process.env.REACT_APP_APPLICATION_FORM_DEV_URL ||
        process.env.REACT_APP_HOSTINGER_APPLICATION_FORM_URL,
        HOSTINGER_DEV_APPLICATION_FORM_URL
    ).replace(/\/+$/g, "");
}

const PUBLIC_PORTAL_FALLBACKS = Object.freeze({
    "financialedge-login.vercel.app": Object.freeze({
        application: "https://financialedge-application.vercel.app",
        broker: LIVE_BROKER_PORTAL_URL,
        lender: `${LIVE_XPORTAL_URL}${LENDER_PORTAL_DEFAULT_PATH}`,
        investor: "https://financialedge-iportal.vercel.app",
        client: "https://financialedge-cportal.vercel.app",
        staff: "https://financialedge-sportal.vercel.app",
    }),
    "balmoral-login.vercel.app": Object.freeze({
        application: "https://financialedge-application.vercel.app",
        broker: LIVE_BROKER_PORTAL_URL,
        lender: `${LIVE_XPORTAL_URL}${LENDER_PORTAL_DEFAULT_PATH}`,
        investor: "https://financialedge-iportal.vercel.app",
        client: "https://financialedge-cportal.vercel.app",
        staff: "https://financialedge-sportal.vercel.app",
    }),
    "balmoral-login-vert.vercel.app": Object.freeze({
        application: "https://financialedge-application.vercel.app",
        broker: LIVE_BROKER_PORTAL_URL,
        lender: `${LIVE_XPORTAL_URL}${LENDER_PORTAL_DEFAULT_PATH}`,
        investor: "https://financialedge-iportal.vercel.app",
        client: "https://financialedge-cportal.vercel.app",
        staff: "https://financialedge-sportal.vercel.app",
    }),
});

function getRuntimePortalFallbackUrl(portalId) {
    if (typeof window === "undefined") {
        return null;
    }

    const hostname = String(window.location?.hostname || "").trim().toLowerCase();
    if (!hostname) {
        return null;
    }

    return PUBLIC_PORTAL_FALLBACKS[hostname]?.[portalId] || null;
}

function getXPortalBaseUrl() {
    return resolveConfiguredUrl(
        process.env.REACT_APP_XPORTAL_URL ||
            process.env.REACT_APP_XPORTAL_BASE_URL,
        LIVE_XPORTAL_URL
    ).replace(/\/+$/g, "");
}

function appendPortalPath(baseUrl, path) {
    const normalisedPath = String(path || "").startsWith("/")
        ? String(path)
        : `/${String(path || "")}`;
    const normalisedBaseUrl = String(baseUrl || "").replace(/\/+$/g, "");

    if (normalisedBaseUrl.endsWith(normalisedPath)) {
        return normalisedBaseUrl;
    }

    return `${normalisedBaseUrl}${normalisedPath}`;
}

function isLocalHostName(value) {
    return ["localhost", "127.0.0.1", "0.0.0.0", "::1", "[::1]"].includes(
        String(value || "").trim().toLowerCase()
    );
}

function isLocalPortalUrl(value) {
    try {
        const url = new URL(String(value || "").trim());
        return isLocalHostName(url.hostname);
    } catch (_error) {
        return false;
    }
}

function isLocalLoginRuntime() {
    if (typeof window === "undefined") {
        return false;
    }

    return isLocalHostName(window.location?.hostname);
}

function resolveLocalConfiguredUrl(values, fallback) {
    const candidates = Array.isArray(values) ? values : [values];

    for (const value of candidates) {
        const resolvedValue = resolveConfiguredUrl(value, "");
        if (resolvedValue && isLocalPortalUrl(resolvedValue)) {
            return resolvedValue.replace(/\/+$/g, "");
        }
    }

    return fallback;
}

function resolveLocalPortalDestinationUrl(portalId) {
    switch (portalId) {
        case PORTAL_IDS.application:
            return resolveLocalConfiguredUrl([
                process.env.REACT_APP_APPLICATION_FORM_BASE_URL,
                process.env.REACT_APP_APPLICATION_FORM_URL,
                process.env.REACT_APP_APP_FORM_URL,
            ], LOCAL_APPLICATION_FORM_URL);
        case PORTAL_IDS.broker:
            return resolveLocalConfiguredUrl(process.env.REACT_APP_BROKER_PORTAL_URL, LOCAL_BROKER_PORTAL_URL);
        case PORTAL_IDS.lender:
            return appendPortalPath(resolveLocalConfiguredUrl([
                process.env.REACT_APP_XPORTAL_URL,
                process.env.REACT_APP_XPORTAL_BASE_URL,
            ], LOCAL_XPORTAL_URL), LENDER_PORTAL_DEFAULT_PATH);
        case PORTAL_IDS.staff:
            return resolveLocalConfiguredUrl(process.env.REACT_APP_STAFF_PORTAL_URL, LOCAL_STAFF_PORTAL_URL);
        case PORTAL_IDS.investor:
            return resolveLocalConfiguredUrl(process.env.REACT_APP_IPORTAL_URL, LOCAL_IPORTAL_URL);
        case PORTAL_IDS.client:
            return resolveLocalConfiguredUrl(process.env.REACT_APP_CLIENT_PORTAL_URL, LOCAL_CLIENT_PORTAL_URL);
        case PORTAL_IDS.referrer:
            return appendPortalPath(resolveLocalConfiguredUrl(process.env.REACT_APP_REFERRER_PORTAL_URL, LOCAL_REFERRER_PORTAL_URL), "/referrers");
        default:
            return "/";
    }
}

function resolvePortalDestinationUrl(portalId) {
    switch (portalId) {
        case PORTAL_IDS.application:
            return resolveConfiguredUrl(
                process.env.REACT_APP_APPLICATION_FORM_BASE_URL ||
                process.env.REACT_APP_APPLICATION_FORM_URL ||
                process.env.REACT_APP_APP_FORM_URL,
                getRuntimePortalFallbackUrl(PORTAL_IDS.application) || "http://localhost:3000"
            );
        case PORTAL_IDS.broker:
            return resolveConfiguredUrl(
                process.env.REACT_APP_BROKER_PORTAL_URL,
                getRuntimePortalFallbackUrl(PORTAL_IDS.broker) || LOCAL_BROKER_PORTAL_URL
            );
        case PORTAL_IDS.lender:
            return appendPortalPath(getXPortalBaseUrl(), LENDER_PORTAL_DEFAULT_PATH);
        case PORTAL_IDS.staff:
            return resolveConfiguredUrl(
                process.env.REACT_APP_STAFF_PORTAL_URL,
                getRuntimePortalFallbackUrl(PORTAL_IDS.staff) || "http://localhost:3010"
            );
        case PORTAL_IDS.investor:
            return resolveConfiguredUrl(
                process.env.REACT_APP_IPORTAL_URL,
                getRuntimePortalFallbackUrl(PORTAL_IDS.investor) || "http://localhost:3013"
            );
        case PORTAL_IDS.client:
            return resolveConfiguredUrl(
                process.env.REACT_APP_CLIENT_PORTAL_URL,
                getRuntimePortalFallbackUrl(PORTAL_IDS.client) || "http://localhost:3011"
            );
        case PORTAL_IDS.referrer:
            return resolveConfiguredUrl(
                process.env.REACT_APP_REFERRER_PORTAL_URL,
                isLocalPortalUrl(getXPortalBaseUrl())
                    ? "http://localhost:3012/referrers"
                    : `${getXPortalBaseUrl()}/referrers`
            );
        default:
            return "/";
    }
}

export function resolveSourceScopedPortalDestinationUrl(portalId, destinationUrl, source = null) {
    const normalisedPortalId = normalisePortalId(portalId);

    if (normalisedPortalId === PORTAL_IDS.application) {
        const devApplicationUrl = resolveDevApplicationFormUrl(source);
        if (devApplicationUrl) {
            return devApplicationUrl;
        }
    }

    if (normalisedPortalId === PORTAL_IDS.broker && isXPortalSource(source)) {
        return isLocalLoginRuntime()
            ? resolveLocalPortalDestinationUrl(PORTAL_IDS.lender)
            : appendPortalPath(getXPortalBaseUrl(), LENDER_PORTAL_DEFAULT_PATH);
    }

    if (normalisedPortalId && isLocalLoginRuntime()) {
        return resolveLocalPortalDestinationUrl(normalisedPortalId);
    }

    return String(destinationUrl || "");
}

function normalisePortal(portal, requestContext = {}) {
    if (!portal || typeof portal !== "object") {
        return null;
    }

    if (!portal.portalId || !portal.destinationUrl) {
        return null;
    }

    const portalId = String(portal.portalId);
    const intent = String(portal.intent || portal.portalId);
    let label = String(portal.label || portal.intent || portal.portalId);
    let description = portal.description ? String(portal.description) : "";

    if (portalId === PORTAL_IDS.broker || intent === PORTAL_IDS.broker) {
        const brokerDetails = getIntentDetails(PORTAL_IDS.broker, requestContext.source);
        label = brokerDetails.label;
        description = brokerDetails.description;
    }

    return {
        portalId,
        intent,
        label,
        description,
        destinationUrl: resolveSourceScopedPortalDestinationUrl(
            portal.portalId,
            portal.destinationUrl,
            requestContext.source
        ),
        routeMode: portal.routeMode ? String(portal.routeMode) : null,
        accessModel: portal.accessModel ? String(portal.accessModel) : null,
    };
}

export function normaliseResolution(rawResolution, requestContext = {}) {
    const allowedPortals = Array.isArray(rawResolution?.allowedPortals)
        ? rawResolution.allowedPortals.map((portal) => normalisePortal(portal, requestContext)).filter(Boolean)
        : [];
    const preferredPortal = normalisePortal(rawResolution?.preferredPortal, requestContext);
    const status = Object.values(RESOLUTION_STATUSES).includes(rawResolution?.status)
        ? rawResolution.status
        : RESOLUTION_STATUSES.unresolved;

    if (status === RESOLUTION_STATUSES.confirmed && allowedPortals.length === 0) {
        logAuthFlowWarning("resolution_invalid_shape", {
            status,
            requestedIntent: requestContext.intent,
            reasonCode: rawResolution?.reasonCode || null,
        });
    }

    return {
        status,
        canonicalUserId: rawResolution?.canonicalUserId || null,
        requestedIntent: rawResolution?.requestedIntent || requestContext.intent || PORTAL_IDS.application,
        allowedPortals,
        preferredPortal,
        accessContext: rawResolution?.accessContext || null,
        reasonCode: rawResolution?.reasonCode || RESOLUTION_REASON_CODES.noConfirmedDestination,
        supportMessage: rawResolution?.supportMessage ?? (status === RESOLUTION_STATUSES.confirmed
            ? null
            : "Please try again shortly. If this continues, contact support."),
        nextAction: rawResolution?.nextAction || RESOLUTION_NEXT_ACTIONS.retry,
        requestId: rawResolution?.requestId || requestContext.requestId || null,
    };
}

export function mapResolutionToStatusReason(resolution) {
    switch (resolution?.reasonCode) {
        case RESOLUTION_REASON_CODES.intentNotAvailable:
            return "not_yet_available";
        case RESOLUTION_REASON_CODES.accountNotFound:
            return "account_not_found";
        case RESOLUTION_REASON_CODES.accessDenied:
        case RESOLUTION_REASON_CODES.noAuthorizedPortals:
            return "access_denied";
        case RESOLUTION_REASON_CODES.noConfirmedDestination:
        default:
            return "access_unconfirmed";
    }
}

function pickFirstValue(...values) {
    for (const value of values) {
        if (value === null || value === undefined) {
            continue;
        }

        if (typeof value === "string" && value.trim() === "") {
            continue;
        }

        return value;
    }

    return null;
}

function extractAccessIdentity(access) {
    const sharedAccessContext = access?.sharedAccessContext || null;
    const portalAccounts = Array.isArray(access?.portals)
        ? access.portals.map((entry) => entry?.access?.account || null).filter(Boolean)
        : [];
    const portalAccountWithEmail = portalAccounts.find((account) => account?.email);
    const portalAccountWithPhone = portalAccounts.find((account) => account?.phone || account?.authPhone);

    return {
        email: pickFirstValue(
            sharedAccessContext?.broker?.user?.email,
            sharedAccessContext?.client?.user?.email,
            sharedAccessContext?.investor?.user?.email,
            sharedAccessContext?.referrer?.user?.email,
            portalAccountWithEmail?.email,
        ),
        phone: pickFirstValue(
            sharedAccessContext?.broker?.user?.phone,
            sharedAccessContext?.client?.user?.phone,
            sharedAccessContext?.investor?.user?.phone,
            sharedAccessContext?.referrer?.user?.phone,
            sharedAccessContext?.application?.user?.authPhone,
            portalAccountWithPhone?.phone,
            portalAccountWithPhone?.authPhone,
        ),
    };
}

function extractIdentity(session, verifiedIdentity = {}) {
    if (!session) {
        return {
            authUserId: verifiedIdentity.authUserId || null,
            phone: verifiedIdentity.phone || null,
            email: verifiedIdentity.email || null,
        };
    }

    const user = session?.user || null;
    const identity = session?.identity || null;
    const accessIdentity = extractAccessIdentity(session?.access);

    return {
        authUserId: pickFirstValue(
            verifiedIdentity.authUserId,
            user?.id,
            identity?.identityId,
            session?.session?.identityId,
            session?.session?.sessionId,
        ),
        phone: pickFirstValue(
            verifiedIdentity.phone,
            user?.phone,
            user?.user_metadata?.phone,
            user?.app_metadata?.phone,
            identity?.phone?.phoneDigits,
            identity?.phone?.phoneE164,
            accessIdentity.phone,
        ),
        email: pickFirstValue(
            verifiedIdentity.email,
            user?.email,
            identity?.email,
            accessIdentity.email,
        ),
    };
}

function mapAllowedPortal(entry, requestContext = {}) {
    const portalId = normalisePortalId(entry?.intent);
    if (!portalId || !entry?.access?.accessGranted) {
        return null;
    }

    const intentDetails = getIntentDetails(portalId, requestContext.source);

    return {
        portalId,
        intent: portalId,
        label: intentDetails.label,
        description: intentDetails.description,
        destinationUrl: resolveSourceScopedPortalDestinationUrl(
            portalId,
            resolvePortalDestinationUrl(portalId),
            requestContext.source
        ),
        routeMode: portalId === PORTAL_IDS.application ? "application" : "portal",
        accessModel: PORTAL_ACCESS_MODELS[portalId] || null,
    };
}

function extractCanonicalUserId(session, payloadIdentity, accessEntries = []) {
    const sessionIdentityId = session?.identity?.identityId || session?.session?.identityId || null;
    if (sessionIdentityId) {
        return sessionIdentityId;
    }

    if (payloadIdentity?.identityId) {
        return payloadIdentity.identityId;
    }

    for (const entry of accessEntries) {
        const account = entry?.access?.account;
        if (!account) {
            continue;
        }

        const candidate =
            account.userId ||
            account.userUID ||
            account.authUserId ||
            account.staffUserId ||
            account.clientUserId ||
            account.clientId ||
            account.brokerageUserId ||
            account.lenderUserId ||
            account.investorUserId ||
            account.referrerUserId;

        if (candidate) {
            return String(candidate);
        }
    }

    return null;
}

function summariseMembership(entry) {
    const portalId = normalisePortalId(entry?.intent);
    const account = entry?.access?.account || null;
    const resource = entry?.access?.resource || null;

    return {
        portalId,
        accessGranted: Boolean(entry?.access?.accessGranted),
        status: entry?.access?.status || "unknown",
        accountId:
            account?.userId ||
            account?.userUID ||
            account?.authUserId ||
            account?.staffUserId ||
            account?.clientUserId ||
            account?.clientId ||
            account?.brokerageUserId ||
            account?.lenderUserId ||
            account?.investorUserId ||
            account?.referrerUserId ||
            null,
        entityId:
            account?.brokerageId ||
            account?.lenderId ||
            account?.investorId ||
            account?.referrerId ||
            resource?.lenderId ||
            resource?.investorId ||
            resource?.referrerId ||
            resource?.applicationId ||
            null,
    };
}

function getResolutionReason({ requestedEntry, allowedPortals, accessEntries }) {
    if (allowedPortals.length > 0) {
        return null;
    }

    if (requestedEntry) {
        if (requestedEntry.access?.status === "missing") {
            return RESOLUTION_REASON_CODES.accountNotFound;
        }

        if (DENIED_ACCESS_STATUSES.has(requestedEntry.access?.status)) {
            return RESOLUTION_REASON_CODES.accessDenied;
        }
    }

    if (accessEntries.some((entry) => DENIED_ACCESS_STATUSES.has(entry?.access?.status))) {
        return RESOLUTION_REASON_CODES.noAuthorizedPortals;
    }

    return RESOLUTION_REASON_CODES.noConfirmedDestination;
}

function getResolutionStatus({ reasonCode, allowedPortals }) {
    if (allowedPortals.length > 0) {
        return RESOLUTION_STATUSES.confirmed;
    }

    if (
        reasonCode === RESOLUTION_REASON_CODES.accountNotFound ||
        reasonCode === RESOLUTION_REASON_CODES.accessDenied ||
        reasonCode === RESOLUTION_REASON_CODES.intentNotAvailable ||
        reasonCode === RESOLUTION_REASON_CODES.noAuthorizedPortals
    ) {
        return RESOLUTION_STATUSES.denied;
    }

    return RESOLUTION_STATUSES.unresolved;
}

function pickPreferredPortal(allowedPortals, { intent, hasExplicitIntent }) {
    if (!allowedPortals.length) {
        return null;
    }

    if (hasExplicitIntent) {
        const requestedPortal = allowedPortals.find((portal) => portal.portalId === intent);
        if (requestedPortal) {
            return requestedPortal;
        }
    }

    const priorityMatch = PORTAL_PRIORITY
        .map((portalId) => allowedPortals.find((portal) => portal.portalId === portalId))
        .find(Boolean);

    return priorityMatch || allowedPortals[0];
}

function buildNextAction(status, allowedPortals) {
    if (status === RESOLUTION_STATUSES.confirmed) {
        return allowedPortals.length === 1
            ? RESOLUTION_NEXT_ACTIONS.launchPortal
            : RESOLUTION_NEXT_ACTIONS.choosePortal;
    }

    return status === RESOLUTION_STATUSES.denied
        ? RESOLUTION_NEXT_ACTIONS.contactSupport
        : RESOLUTION_NEXT_ACTIONS.retry;
}

function buildSupportMessage(status, reasonCode) {
    if (status === RESOLUTION_STATUSES.confirmed) {
        return null;
    }

    if (status === RESOLUTION_STATUSES.denied) {
        return "If you believe this should be available, contact your administrator or support team.";
    }

    if (reasonCode === RESOLUTION_REASON_CODES.intentNotAvailable) {
        return "This destination is recognised, but it is not available yet.";
    }

    return "Please try again shortly. If this continues, contact support.";
}

function buildAccessContext({ requestContext, session, payloadIdentity, payloadAccess, accessEntries, allowedPortals }) {
    const identity = extractIdentity(session);

    return {
        requestedIntent: requestContext.intent,
        source: requestContext.source || null,
        returnTo: requestContext.returnTo || null,
        identities: {
            authUserId: identity.authUserId || payloadIdentity?.identityId || null,
            phoneVerified: Boolean(payloadIdentity?.phone?.phoneDigits || payloadIdentity?.phone?.phoneE164 || identity.phone),
            emailVerified:
                Boolean(identity.email) ||
                Boolean(payloadIdentity?.contacts?.backupEmail?.verified) ||
                Boolean(payloadIdentity?.contacts?.backupEmail?.available),
        },
        userExists: accessEntries.some((entry) => Boolean(entry?.access?.account)),
        allowedPortalIds: allowedPortals.map((portal) => portal.portalId),
        memberships: accessEntries.map(summariseMembership),
        sharedAccessContext: payloadAccess?.sharedAccessContext || null,
        applicationForm: payloadAccess?.applicationForm || null,
        blocked: payloadAccess?.blocked === true,
        blockReason: payloadAccess?.blockReason || null,
    };
}

function mapAccessPayloadToResolution(payload, requestContext) {
    const payloadAccess = payload?.access || {};
    const payloadIdentity = payload?.identity || null;
    const accessEntries = Array.isArray(payloadAccess?.portals)
        ? payloadAccess.portals.filter((entry) => normalisePortalId(entry?.intent))
        : [];
    const allowedPortals = accessEntries
        .filter((entry) => entry?.access?.accessGranted)
        .map((entry) => mapAllowedPortal(entry, requestContext))
        .filter(Boolean);
    const requestedEntry = accessEntries.find((entry) => entry.intent === requestContext.intent) || null;
    const reasonCode = getResolutionReason({
        requestedEntry,
        allowedPortals,
        accessEntries,
    });
    const status = getResolutionStatus({ reasonCode, allowedPortals });

    return normaliseResolution({
        status,
        canonicalUserId: extractCanonicalUserId(requestContext.session, payloadIdentity, accessEntries),
        requestedIntent: requestContext.intent,
        allowedPortals,
        preferredPortal: pickPreferredPortal(allowedPortals, requestContext),
        accessContext: buildAccessContext({
            requestContext,
            session: requestContext.session,
            payloadIdentity,
            payloadAccess,
            accessEntries,
            allowedPortals,
        }),
        reasonCode,
        supportMessage: buildSupportMessage(status, reasonCode),
        nextAction: buildNextAction(status, allowedPortals),
        requestId: requestContext.requestId,
    }, requestContext);
}

export async function resolveBackendAccess({
    session,
    intent,
    hasExplicitIntent,
    source,
    returnTo,
    verifiedPhone = null,
    verifiedEmail = null,
    authUserId = null,
}) {
    const identity = extractIdentity(session, {
        authUserId,
        phone: verifiedPhone,
        email: verifiedEmail,
    });
    const sessionIdentityId = session?.identity?.identityId || session?.session?.identityId || authUserId || null;

    logAuthFlowInfo("resolution_started", {
        authUserId: identity.authUserId,
        phone: identity.phone,
        email: identity.email,
        intent,
        source,
        hasExplicitIntent,
    });

    const response = await fetch(`${getApiBaseUrl()}/api/auth/v2/access/resolve`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            session: sessionIdentityId ? { identityId: sessionIdentityId } : null,
            authUserId: identity.authUserId,
            phone: identity.phone,
            email: identity.email,
            intent: hasExplicitIntent ? intent : null,
            hasExplicitIntent,
            source,
            returnTo,
        }),
    });

    const payload = await response.json().catch(() => ({}));
    const requestId = getResponseRequestId(response, payload);

    if (!response.ok) {
        logAuthFlowWarning("resolution_request_failed", {
            intent,
            source,
            statusCode: response.status,
            requestId,
        });
        throw new Error(payload?.message || "Access resolution request failed.");
    }

    const resolution = payload?.resolution
        ? normaliseResolution(payload.resolution, {
            intent,
            source,
            returnTo,
            requestId,
        })
        : mapAccessPayloadToResolution(payload, {
            intent,
            hasExplicitIntent,
            source,
            returnTo,
            requestId,
            session,
        });

    if (resolution.status === RESOLUTION_STATUSES.confirmed) {
        logAuthFlowInfo("resolution_success", {
            intent,
            status: resolution.status,
            reasonCode: resolution.reasonCode,
            allowedPortalIds: resolution.allowedPortals.map((portal) => portal.portalId).join(","),
            preferredPortalId: resolution.preferredPortal?.portalId || null,
            requestId: resolution.requestId,
        });
    } else {
        logAuthFlowWarning("resolution_non_confirmed", {
            intent,
            status: resolution.status,
            reasonCode: resolution.reasonCode,
            nextAction: resolution.nextAction,
            requestId: resolution.requestId,
        });
    }

    return resolution;
}
